/**
 * Help System Mutation Hooks
 * React Query hooks for help article mutations (feedback, view tracking)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { submitFeedback, recordArticleView } from '../services/helpApi';
import { helpKeys } from './useHelp';

/**
 * Hook for help article mutations (feedback, view tracking)
 */
export const useHelpMutations = () => {
  const queryClient = useQueryClient();

  /**
   * Submit feedback (helpful/not helpful) for an article
   */
  const submitFeedbackMutation = useMutation({
    mutationFn: ({ articleId, helpful }: { articleId: string; helpful: boolean }) =>
      submitFeedback(articleId, helpful),
    onSuccess: (_, variables) => {
      // Invalidate the specific article to get updated feedback counts
      queryClient.invalidateQueries({
        queryKey: helpKeys.articleDetail(variables.articleId)
      });
      // Also invalidate popular articles as feedback affects popularity
      queryClient.invalidateQueries({
        queryKey: helpKeys.popular(10)
      });
    },
  });

  /**
   * Record article view
   */
  const recordViewMutation = useMutation({
    mutationFn: (articleId: string) => recordArticleView(articleId),
    onSuccess: (_, articleId) => {
      // Invalidate the specific article to get updated view count
      queryClient.invalidateQueries({
        queryKey: helpKeys.articleDetail(articleId)
      });
      // Also invalidate popular articles as views affect popularity
      queryClient.invalidateQueries({
        queryKey: helpKeys.popular(10)
      });
    },
  });

  return {
    submitFeedback: submitFeedbackMutation,
    recordView: recordViewMutation,
    isLoading: submitFeedbackMutation.isPending || recordViewMutation.isPending,
  };
};

export default useHelpMutations;
